import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useServiceTemplates,
  useCreateServiceTemplate,
  useUpdateServiceTemplate,
  useDeleteServiceTemplate,
} from '@/hooks/use-service-templates';
import { toast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2, Edit3, Package, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const CATEGORIES = ['Websites', 'SEO', 'Design', 'Hosting', 'Maintenance', 'Marketing', 'Other'];

interface ServiceTemplateManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ServiceTemplateManager({ open, onOpenChange }: ServiceTemplateManagerProps) {
  const { data, isLoading } = useServiceTemplates();
  const createTemplate = useCreateServiceTemplate();
  const updateTemplate = useUpdateServiceTemplate();
  const deleteTemplate = useDeleteServiceTemplate();

  const templates = data?.data || [];

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [category, setCategory] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setDescription('');
    setUnitPrice('');
    setCategory('');
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !unitPrice) return;

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      unitPrice: parseFloat(unitPrice),
      category: category === '_none' ? undefined : (category || undefined),
    };

    try {
      if (editingId) {
        await updateTemplate.mutateAsync({ id: editingId, ...data });
        toast({ title: 'Template updated', variant: 'success' });
      } else {
        await createTemplate.mutateAsync(data);
        toast({ title: 'Template added', description: `\"${data.name}\" is now available in the catalogue.`, variant: 'success' });
      }
      resetForm();
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err?.message, variant: 'destructive' });
    }
  };

  const handleEdit = (t: any) => {
    setName(t.name);
    setDescription(t.description || '');
    setUnitPrice(String(t.unitPrice));
    setCategory(t.category || '');
    setEditingId(t.id);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate.mutateAsync(id);
      toast({ title: 'Template removed', variant: 'success' });
    } catch (err: any) {
      toast({ title: 'Failed to delete', description: err?.message, variant: 'destructive' });
    }
  };

  // Group by category
  const grouped: Record<string, any[]> = {};
  templates.forEach((t: any) => {
    const cat = t.category || 'Uncategorised';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetForm(); }}>
      <DialogContent className="sm:max-w-[600px] max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Package size={18} className="text-dyndev-green" />
            <DialogTitle>Service Catalogue</DialogTitle>
          </div>
          <DialogDescription>
            Manage your frequently used services. These appear as quick-add options when creating quotes and invoices.
          </DialogDescription>
        </DialogHeader>

        {/* Add / Edit form */}
        <form onSubmit={handleSubmit} className="space-y-3 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">
              {editingId ? 'Edit Template' : 'Add New Template'}
            </h4>
            {editingId && (
              <button type="button" onClick={resetForm} className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                Cancel edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Name *">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. E-commerce Website"
              />
            </FormField>
            <FormField label="Price (£) *">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="e.g. 2500"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Category">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Description">
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description (optional)"
              />
            </FormField>
          </div>

          <Button
            type="submit"
            size="sm"
            disabled={createTemplate.isPending || updateTemplate.isPending || !name.trim() || !unitPrice}
            className="gap-1.5"
          >
            {(createTemplate.isPending || updateTemplate.isPending) && <Loader2 size={14} className="animate-spin" />}
            {editingId ? (
              <><Check size={14} /> Update</>
            ) : (
              <><Plus size={14} /> Add to Catalogue</>
            )}
          </Button>
        </form>

        {/* Template list */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <Loader2 size={24} className="animate-spin text-dyndev-green mx-auto" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <Package size={32} className="text-[var(--text-secondary)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-secondary)]">No services in the catalogue yet.</p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">Add your first template above.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                  {cat}
                </h4>
                <div className="space-y-1.5">
                  {items.map((t: any) => (
                    <div
                      key={t.id}
                      className={cn(
                        'flex items-center gap-3 p-2.5 rounded-lg border border-[var(--border-color)] hover:border-dyndev-green/20 transition-colors group',
                        !t.isActive && 'opacity-50'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{t.name}</span>
                          {!t.isActive && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 text-gray-400">Hidden</Badge>
                          )}
                        </div>
                        {t.description && (
                          <p className="text-xs text-[var(--text-secondary)] truncate">{t.description}</p>
                        )}
                      </div>
                      <span className="text-sm font-mono font-bold text-dyndev-green whitespace-nowrap">
                        £{t.unitPrice.toFixed(2)}
                      </span>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(t)}
                          className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
                          title="Edit"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(t.id)}
                          className="p-1 rounded hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
